import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Building2,
  QrCode,
  Wallet as WalletIcon,
  Sparkles,
  Clock,
  Plus,
} from "lucide-react";
import { formatINR, formatHour, formatApiError } from "@/lib/format";
import { toast } from "sonner";

export default function CustomerDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [hubs, setHubs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [wallet, setWallet] = useState(0);

  useEffect(() => {
    (async () => {
      const [h, b, m, w] = await Promise.all([
        api.get("/hubs"),
        api.get("/bookings/me"),
        api.get("/memberships/me"),
        api.get("/wallet/me"),
      ]);
      setHubs(h.data);
      setBookings(b.data);
      setMemberships(m.data);
      setWallet(w.data.balance);
    })();
  }, []);

  const active = bookings.find((b) => b.status === "checked_in");
  const upcoming = bookings.filter((b) => b.status === "booked");

  return (
    <div className="space-y-8" data-testid="customer-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-[#E8D6A3] mb-3">
            Hello, {user?.name?.split(" ")[0]}
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
            Book · Access · Rise.
          </h1>
        </div>
        <Button
          onClick={() => nav("/app/hubs")}
          data-testid="dashboard-find-hub-btn"
          className="bg-[#D4B878] hover:bg-[#E8D6A3] rounded-full px-6"
        >
          Find a Hub <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>

      {/* Active session */}
      {active && (
        <div className="rounded-3xl border border-[#D4B878]/40 bg-gradient-to-br from-[#1a1508] to-[#0c0c0c] p-8 ml-glow">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-2 h-2 rounded-full bg-[#D4B878] ml-pulse" />
            <span className="uppercase tracking-[0.22em] text-xs text-[#E8D6A3] font-bold">
              Active session
            </span>
          </div>
          <div className="text-3xl font-black tracking-tight mb-2">
            {active.resource_label}
          </div>
          <div className="text-zinc-400">
            {active.hub_name} · {formatHour(active.start_hour)} to{" "}
            {formatHour(active.start_hour + active.duration_hours)}
          </div>
          <div className="mt-5 flex gap-3">
            <Button
              onClick={async () => {
                await api.post(`/bookings/${active.id}/check-out`);
                const b = await api.get("/bookings/me");
                setBookings(b.data);
              }}
              data-testid="dashboard-check-out-btn"
              className="bg-white text-black hover:bg-zinc-200 rounded-full"
            >
              Check out
            </Button>
          </div>
        </div>
      )}

      {/* Stat grid */}
      <div className="grid md:grid-cols-4 gap-5">
        <StatCard
          icon={<WalletIcon />}
          label="Wallet"
          value={formatINR(wallet)}
          hint="Top up anytime"
          onClick={() => nav("/app/wallet")}
          testid="stat-wallet"
        />
        <StatCard
          icon={<Sparkles />}
          label="Active plans"
          value={memberships.filter((m) => m.status === "active").length}
          hint="Memberships"
          onClick={() => nav("/app/memberships")}
          testid="stat-memberships"
        />
        <StatCard
          icon={<Clock />}
          label="Upcoming"
          value={upcoming.length}
          hint="Bookings"
          onClick={() => nav("/app/bookings")}
          testid="stat-upcoming"
        />
        <StatCard
          icon={<Building2 />}
          label="Hubs live"
          value={hubs.length}
          hint="Across India"
          onClick={() => nav("/app/hubs")}
          testid="stat-hubs"
        />
      </div>

      {/* Quick hubs */}
      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-5">Nearby hubs</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {hubs.slice(0, 6).map((h) => (
            <button
              key={h.id}
              data-testid={`dashboard-hub-${h.city.toLowerCase()}`}
              onClick={() => nav(`/app/hubs/${h.id}`)}
              className="text-left rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.04] transition"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-[#E8D6A3] mb-2">
                {h.format}
              </div>
              <div className="text-xl font-bold">{h.name}</div>
              <div className="text-sm text-zinc-500 mt-1">{h.city}, {h.state}</div>
              <div className="mt-5 flex items-center justify-between">
                <div>
                  <div className="text-2xl font-black text-white">
                    {h.available_now}
                    <span className="text-sm font-semibold text-zinc-500">
                      /{h.total_capacity}
                    </span>
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                    Available now
                  </div>
                </div>
                <ArrowRight size={18} className="text-zinc-500" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* QR helper */}
      <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-8 flex items-center justify-between gap-6 flex-wrap">
        <div className="flex items-center gap-5">
          <div className="p-4 rounded-2xl bg-[#D4B878]/10 text-[#E8D6A3]">
            <QrCode size={28} />
          </div>
          <div>
            <div className="font-bold text-lg">QR check-in at the hub</div>
            <div className="text-zinc-400 text-sm">
              Scan at the counter · your session starts automatically.
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          data-testid="dashboard-show-qr-btn"
          className="bg-white/5 border-white/10 rounded-full"
          onClick={() => nav("/app/bookings")}
        >
          View my QR
        </Button>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, hint, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="text-left rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.04] transition"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="p-2 rounded-lg bg-white/5 text-[#E8D6A3]">{icon}</div>
      </div>
      <div className="text-3xl font-black">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-[0.18em] text-zinc-500">
        {label}
      </div>
      <div className="text-xs text-zinc-600 mt-1">{hint}</div>
    </button>
  );
}

function ActiveSessionCard({ booking, onChange }) {
  const [now, setNow] = useState(new Date());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const endTime = useMemo(() => {
    const d = new Date(`${booking.date}T00:00:00`);
    d.setHours(booking.start_hour + booking.duration_hours, 0, 0, 0);
    return d;
  }, [booking]);

  const diffSec = Math.max(0, Math.floor((endTime.getTime() - now.getTime()) / 1000));
  const hh = String(Math.floor(diffSec / 3600)).padStart(2, "0");
  const mm = String(Math.floor((diffSec % 3600) / 60)).padStart(2, "0");
  const ss = String(diffSec % 60).padStart(2, "0");

  const totalSec = booking.duration_hours * 3600;
  const elapsed = totalSec - diffSec;
  const pct = Math.max(0, Math.min(100, (elapsed / totalSec) * 100));

  const extend = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/extend`, { additional_hours: 1 });
      toast.success("Session extended by 1 hour");
      await onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const checkOut = async () => {
    await api.post(`/bookings/${booking.id}/check-out`);
    await onChange();
  };

  return (
    <div
      className="rounded-3xl border border-[#D4B878]/40 bg-gradient-to-br from-[#1a1508] to-[#0c0c0c] p-8 ml-glow"
      data-testid="dashboard-active-session-card"
    >
      <div className="flex items-center gap-3 mb-3">
        <span className="w-2 h-2 rounded-full bg-[#D4B878] ml-pulse" />
        <span className="uppercase tracking-[0.22em] text-xs text-[#E8D6A3] font-bold">
          Active session
        </span>
      </div>
      <div className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <div className="text-3xl font-black tracking-tight mb-1">
            {booking.resource_label}
          </div>
          <div className="text-zinc-400">
            {booking.hub_name} · {formatHour(booking.start_hour)} →{" "}
            {formatHour(booking.start_hour + booking.duration_hours)}
          </div>
        </div>
        <div className="text-right" data-testid="dashboard-session-timer">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1">
            Time remaining
          </div>
          <div className="text-4xl md:text-5xl font-black tabular-nums text-[#E8D6A3]">
            {hh}:{mm}:{ss}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-6 h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#D4B878] to-[#E8D6A3] transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="mt-6 flex gap-3 flex-wrap">
        <Button
          onClick={extend}
          disabled={busy}
          data-testid="dashboard-extend-btn"
          className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
        >
          <Plus size={14} className="mr-2" />
          Extend 1 hour
        </Button>
        <Button
          onClick={checkOut}
          variant="outline"
          data-testid="dashboard-check-out-btn"
          className="bg-white/5 border-white/10 rounded-full"
        >
          Check out
        </Button>
      </div>
    </div>
  );
}
